import { FeatureFace, isFeatureFace } from './FeatureFace';

/**
 * 判断对象是否为特征曲面对象。
 *
 * @param value 待判断对象。
 * @returns 是否为特征曲面对象。
 */
export function isFeatureSurface(value: unknown): value is FeatureSurface {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Reflect.get(value, 'isFeatureSurface') === true;
}

/**
 * 判断对象是否为可提供平面几何的正式特征面。
 *
 * 新拓扑工厂通常把 plane 建成 FeatureFace，但旧快照或外部 provider 仍可能返回
 * `FeatureSurface(surfaceKind='plane')`；统一守卫兼容两条正式链路，同时拒绝圆柱、圆锥和样条面。
 *
 * @param value 待判断对象。
 * @returns 普通 FeatureFace 或 plane FeatureSurface 时返回 true。
 */
export function isPlanarFeatureFace(value: unknown): value is FeatureFace {
  return isFeatureFace(value) && value.isPlanarGeometry();
}

/**
 * 特征曲面对象。
 *
 * 设计说明：
 * 1. 继承 `FeatureFace`，复用既有面选择、边界线联动和拓扑身份；
 * 2. 额外保存 worker 解析出的底层曲面类型；
 * 3. 曲面参数保持可序列化，避免将 Puppy CAD 内核 wasm 对象泄漏到主线程生命周期。
 */
export class FeatureSurface extends FeatureFace {
  /** 运行时类型标记。 */
  public readonly isFeatureSurface: boolean = true;

  /**
   * 构造函数。
   *
   * @param input 构造输入。
   */
  public constructor(input: ConstructorParameters<typeof FeatureFace>[0] & {
    /** 底层曲面类型。 */
    surfaceKind?: TopologySurfaceKind;
  }) {
    const surfaceKind = input.surfaceKind ?? 'other';
    super({...input, surfaceKind});
    this.name = `FeatureSurface:${input.faceIndex}:${surfaceKind}`;
  }
}
